import Layout from "@/HOC";
import { Col, Row } from "antd";
import React, { useEffect, useState } from "react";
import styles from "./pricing.module.css";
import { SVG } from "@/assets/svg";
import { IPackage } from "@/interfaces/package.interface";
import { getPackages } from "@/utils/apis/packages/get-packages.api";
import ListView from "@/component/list";
import { getTimeInterval } from "@/utils/common";
import { useRouter } from "next/router";
import PackageSkeleton from "@/component/loader/skeletons/PackageSkeleton";
import Head from "next/head";
import { getPaymentSeo } from "@/utils/apis/minerals/article-seo.api";
import { DefaultCanonical, PageKeywords, metaDescription, metaTitle } from "@/component/seo/DefaultSeo";

function Pricing() {
  const [packages, setPackages] = useState<IPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [pricingSeo, setPricingSeo] = useState<any>(null);

  const router = useRouter();
  const currentPageName = router.pathname.replace(/^\//, "").replace(/-/g, " ");
  const formattedPageName =
    currentPageName.charAt(0).toUpperCase() + currentPageName.slice(1);

  const fetchPricingSeo = async () => {
    try {
      const response = await getPaymentSeo();
      if (response.data.success) {
        setPricingSeo(response.data.data);
      }
    } catch (error) {
      console.log("<fetchPricingSeo> Error", error);
    }
  };

  const fetchPackages = async () => {
    setLoading(true);
    const res = await getPackages();
    if (res.data.success) {
      const sortedByAmount = res.data.data.sort(
        (a: IPackage, b: IPackage) => Number(a.amount) - Number(b.amount)
      );
      setPackages(sortedByAmount);
    } else {
      console.log(res.data.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPackages();
    fetchPricingSeo();
  }, []);

  const handlePackageClick = (e: any, item: IPackage) => {
    e.preventDefault();
    router.push(
      {
        pathname: "signUp",
        query: {
          selectedPackage: JSON.stringify(item),
        },
      },
      `/signUp?packageId=${item._id}`
    );
  };


  const originalTitle = pricingSeo?.title || formattedPageName;
  const seoTitle = [
    originalTitle,
    pricingSeo?.description || "",
    pricingSeo?.keywords || "",
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <div>
      <DefaultCanonical url={pricingSeo?.canonicalUrl} />
      <PageKeywords keywords={pricingSeo?.keywords} title={originalTitle} description={pricingSeo?.description || ""} />
      <Head>
        <title key="title">{metaTitle(originalTitle, pricingSeo?.title)}</title>
        <meta
          key="description"
          name="description"
          content={metaDescription(pricingSeo?.description || "", pricingSeo?.description)}
        />
        <meta
          key="og:title"
          property="og:title"
          content={pricingSeo?.title || formattedPageName}
        />
        <meta
          key="og:description"
          property="og:description"
          content={metaDescription(pricingSeo?.description || "", pricingSeo?.description)}
        />
        <meta key="og:type" property="og:type" content="article" />
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta
          key="twitter:title"
          name="twitter:title"
          content={pricingSeo?.title || formattedPageName}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={pricingSeo?.description || ""}
        />
        <meta
          key="viewport"
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
      </Head>
      <Layout>
        {/* <div>
          <h4 className={`${styles.choose}`}>Please choose a plan to signup</h4>
        </div> */}
        <div className="main">
          <div className={`${styles.Main}`}>
            <div className={`${styles.Upload}`}>
              <h6 className={`${styles.otherImages}`}>Plans and Features</h6>
            </div>
            {/* <div>
              <p className={`${styles.paid}`}>
                We’re here to help you with our subscription plans.
              </p>
            </div> */}
            <div className={`${styles.layout}`}>
              <Row gutter={[32, 32]}>
                <ListView
                  data={packages}
                  loading={loading}
                  loader={<PackageSkeleton />}
                  renderItem={(item: IPackage) => {
                    const isFreePlan = parseInt(item.amount) === 0;

                    return (
                      <>
                        <Col
                          onClick={(e) => handlePackageClick(e, item)}
                          className={`payPlan`}
                          md={12}
                        >
                          {item?.tag && (
                            <Col className={`${styles.downgrade}`}>
                              <button className={`${styles.premiumplan}`}>
                                {item.tag}
                              </button>
                            </Col>
                          )}
                          {isFreePlan ? (
                            <h2 className={`${styles.dollar}`}>$0</h2>
                          ) : (
                            <h2 className={`${styles.dollar}`}>
                              ${item.amount}{" "}
                              {!isFreePlan && (
                                <span>/{getTimeInterval(item.Tenure)}</span>
                              )}{" "}
                            </h2>
                          )}
                          <h1 className={`${styles.basic}`}>
                            {item.packageName}
                          </h1>
                          {item.description && (
                            <h6 className={`${styles.doc}`}>
                              {item.description}
                            </h6>
                          )}
                          {(item.numberOfMineral === "unlimited" ||
                            parseInt(item.numberOfMineral) > 0) && (
                            <h3 className={`${styles.current}`}>
                              <SVG.CheckCircle
                                style={{ width: "20px", margin: " 0px 10px" }}
                              />
                              {/* Post Mineral(s) ({item.numberOfMineral}) */}
                              {item.numberOfMineral === "unlimited"
                                ? "Unlimited Cataloging"
                                : `Post Mineral(s) (${item.numberOfMineral})`}
                            </h3>
                          )}

                          {item?.printSpecimenLabel && (
                            <h3 className={`${styles.current}`}>
                              <SVG.CheckCircle
                                style={{ width: "20px", margin: " 0px 10px" }}
                              />{" "}
                              Print Specimen Label{" "}
                            </h3>
                          )}

                          {item?.photosPerSpecimen && (
                            <h3 className={`${styles.current}`}>
                              <SVG.CheckCircle
                                style={{ width: "20px", margin: " 0px 10px" }}
                              />{" "}
                              {item.photosPerSpecimen} Photos per Specimen
                            </h3>
                          )}
                          {!!item?.videoPerSpecimen && (
                            <h3 className={`${styles.current}`}>
                              <SVG.CheckCircle
                                style={{ width: "20px", margin: " 0px 10px" }}
                              />{" "}
                              {item?.videoPerSpecimen} Videos per Specimen
                            </h3>
                          )}

                          {item.exportReport && (
                            <h3 className={`${styles.current}`}>
                              <SVG.CheckCircle
                                style={{ width: "20px", margin: " 0px 10px" }}
                              />{" "}
                              Export Catalog Report
                            </h3>
                          )}

                          {item.noBanner && (
                            <h3 className={`${styles.current}`}>
                              <SVG.CheckCircle
                                style={{ width: "20px", margin: " 0px 10px" }}
                              />
                              Remove Advertisements
                            </h3>
                          )}

                          {item.bulkMineralUpload && (
                            <h3 className={`${styles.current}`}>
                              <SVG.CheckCircle
                                style={{ width: "20px", margin: " 0px 10px" }}
                              />
                              Import Specimens
                            </h3>
                          )}

                          <button className="Current"> Choose plan</button>
                        </Col>
                      </>
                    );
                  }}
                />
              </Row>
            </div>
          </div>
        </div>
      </Layout>
    </div>
  );
}

export default Pricing;
